import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { ASSETS } from "@/lib/brand";
import ChefUploadForm from "@/components/registry/ChefUploadForm";

const API = `${window.location.origin}/api`;

const GOLD = "#C4A671";

const inputCls =
  "w-full bg-[#111] border border-[#2a2a2a] px-4 py-3 text-[14px] text-[#F2EFE8] outline-none focus:border-[#C4A671] transition-colors";

export default function VelvetWall({ open, onClose, initialCode = "" }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState("");
  const [unlocked, setUnlocked] = useState(null);
  const [leadMode, setLeadMode] = useState(false);
  const [sent, setSent] = useState(false);
  const [lead, setLead] = useState({ nome: "", azienda: "", citta: "", email: "", telefono: "" });
  const autoTried = useRef(false);

  const set = (k) => (e) => setLead((p) => ({ ...p, [k]: e.target.value }));

  const verify = async (value) => {
    const c = (value ?? code).trim();
    if (c.length < 2) return;
    setBusy(true);
    setDenied("");
    try {
      const { data } = await axios.post(`${API}/registry/access-request`, { code: c });
      setUnlocked({ ...data, code: c.toUpperCase() });
    } catch (err) {
      setDenied(
        err?.response?.data?.detail ||
          "Codice non riconosciuto. L'accesso al Registro è riservato alle cucine partner con un'Allocazione attiva."
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open && initialCode && !autoTried.current) {
      autoTried.current = true;
      const c = initialCode.toUpperCase();
      setCode(c);
      verify(c);
    }
  }, [open, initialCode]); // eslint-disable-line

  const sendLead = async () => {
    if (!lead.nome.trim() || !lead.email.trim()) {
      toast.error("Nome ed email sono necessari.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/sommelier-club`, {
        ...lead,
        canale: "Ho.Re.Ca.",
        messaggio: "Richiesta apertura posizione commerciale — Registro Allocazioni d'Autore",
      });
      setSent(true);
    } catch {
      toast.error("Invio non riuscito. Riprovi tra qualche istante.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-5 overflow-y-auto"
          style={{ background: "rgba(5,5,5,0.94)", backdropFilter: "blur(14px)" }}
          data-testid="velvet-wall-modal"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg border px-7 sm:px-12 py-11 my-auto"
            style={{ borderColor: "rgba(196,166,113,0.4)", background: "#0A0A0A" }}
          >
            <button
              data-testid="velvet-wall-close"
              onClick={onClose}
              aria-label="Chiudi"
              className="absolute top-4 right-4 p-2 text-[#7A7568] hover:text-[#C4A671] transition-colors"
            >
              <X size={18} />
            </button>

            <img src={ASSETS.logo} alt="" className="h-12 w-12 mx-auto mb-6 object-contain" />

            {unlocked ? (
              <ChefUploadForm code={unlocked.code} info={unlocked} onClose={onClose} />
            ) : sent ? (
              <div className="text-center" data-testid="velvet-wall-sent">
                <h3 className="font-serif-display text-[26px] text-[#F2EFE8]">Richiesta ricevuta.</h3>
                <p className="mt-4 text-[14px] leading-[1.85] font-light" style={{ color: "#C9C3B5" }}>
                  La Maison esaminerà la Sua posizione con la massima riservatezza e
                  la contatterà personalmente.
                </p>
                <button
                  onClick={onClose}
                  className="mt-8 px-8 py-3 text-[10px] tracking-[0.24em] uppercase"
                  style={{ background: GOLD, color: "#0A0A0A" }}
                >
                  Chiudi
                </button>
              </div>
            ) : leadMode ? (
              <div data-testid="velvet-wall-lead-form">
                <h3 className="font-serif-display text-[24px] text-[#F2EFE8] text-center">
                  Apertura posizione commerciale
                </h3>
                <p className="mt-3 mb-7 text-[13px] leading-[1.8] font-light text-center" style={{ color: "#C9C3B5" }}>
                  Le allocazioni vengono aperte su valutazione della Maison.
                  Ci lasci i Suoi riferimenti: sarà ricontattato in via riservata.
                </p>
                <div className="flex flex-col gap-3">
                  <input data-testid="lead-nome" className={inputCls} placeholder="Nome e cognome *" value={lead.nome} onChange={set("nome")} />
                  <input data-testid="lead-azienda" className={inputCls} placeholder="Ristorante / Azienda" value={lead.azienda} onChange={set("azienda")} />
                  <input data-testid="lead-citta" className={inputCls} placeholder="Città" value={lead.citta} onChange={set("citta")} />
                  <input data-testid="lead-email" type="email" className={inputCls} placeholder="Email *" value={lead.email} onChange={set("email")} />
                  <input data-testid="lead-telefono" className={inputCls} placeholder="Telefono" value={lead.telefono} onChange={set("telefono")} />
                </div>
                <button
                  data-testid="lead-submit"
                  onClick={sendLead}
                  disabled={busy}
                  className="mt-6 w-full py-3.5 text-[10px] tracking-[0.24em] uppercase transition disabled:opacity-40"
                  style={{ background: GOLD, color: "#0A0A0A" }}
                >
                  {busy ? "Invio…" : "Invia la richiesta"}
                </button>
                <button
                  onClick={() => setLeadMode(false)}
                  className="mt-4 w-full overline text-[9px] text-[#7A7568] hover:text-[#C9C3B5] transition-colors"
                >
                  ← Ho un Codice di Allocazione
                </button>
              </div>
            ) : (
              <div data-testid="velvet-wall-code-form">
                <span className="block text-center overline text-[9px]" style={{ color: "#8B7A55" }}>
                  Registro Globale delle Allocazioni
                </span>
                <h3 className="mt-3 font-serif-display text-[26px] leading-tight text-[#F2EFE8] text-center">
                  Registri la Sua <span className="italic font-light" style={{ color: GOLD }}>creazione.</span>
                </h3>
                <p className="mt-4 mb-8 text-[13.5px] leading-[1.85] font-light text-center" style={{ color: "#C9C3B5" }}>
                  Il Registro accoglie esclusivamente le creazioni nate da
                  un&apos;Allocazione attiva della Maison. Inserisca il Suo Codice
                  di Allocazione per procedere.
                </p>
                <label className="block">
                  <span className="overline text-[9px] text-[#7A7568] block mb-2">Codice di Allocazione</span>
                  <input
                    data-testid="allocation-code-input"
                    className={`${inputCls} uppercase tracking-[0.28em] text-center font-medium`}
                    placeholder="IBLEI-XXXX-NN"
                    value={code}
                    maxLength={20}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && verify()}
                  />
                </label>
                {denied && (
                  <p
                    data-testid="allocation-code-denied"
                    className="mt-4 text-[12.5px] italic leading-[1.7] text-center font-light"
                    style={{ color: "#B08D57" }}
                  >
                    {denied}
                  </p>
                )}
                <button
                  data-testid="allocation-code-submit"
                  onClick={() => verify()}
                  disabled={busy || code.trim().length < 2}
                  className="mt-6 w-full py-3.5 text-[10px] tracking-[0.24em] uppercase transition disabled:opacity-40"
                  style={{ background: GOLD, color: "#0A0A0A" }}
                >
                  {busy ? "Verifica…" : "Verifica il Codice"}
                </button>
                <div className="mt-7 pt-6 border-t border-[#1c1c1c] text-center">
                  <button
                    data-testid="velvet-wall-request-position"
                    onClick={() => setLeadMode(true)}
                    className="overline text-[9.5px] transition-colors hover:opacity-80"
                    style={{ color: GOLD }}
                  >
                    Non possiede un&apos;Allocazione? Richieda l&apos;apertura di una posizione commerciale →
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
